/* eslint-disable no-underscore-dangle */
import 'core-js/es/promise'; // For IE 11 support
import 'core-js/es/array'; // For IE 11 support
import 'core-js/es/object/assign'; // For IE 11 support
import 'core-js/es/object/entries'; // For IE 11 support
import 'core-js/es/string/starts-with'; // For IE 11 support
import 'core-js/es/string/ends-with'; // For IE 11 support
import 'core-js/es/string/includes'; // For IE 11 support
import 'core-js/es/number/parse-int'; // For IE11 support (in ReactDOM)
import React from 'react';
import thunk from 'redux-thunk';
import ReactDOM from 'react-dom';
import { ApolloProvider } from '@apollo/client';
import '@homeaway/edap-integrations';
import '@homeaway/stab-analytics-polyfill/release/javascripts/stab-analytics-polyfill.min';
import { createStore, applyMiddleware, compose } from 'redux';
import { Provider } from 'react-redux';
import { routerMiddleware } from 'connected-react-router';
import StorageMiddleware from '@homeaway/redux-local-storage-middleware';
import TagManager from 'react-gtm-module';
import { loadableReady } from '@loadable/component';
import { createClientSideReducer } from '../universal/reducers';
import App, { history } from '../universal/components/App/ClientApp';
import ValidationMiddleware from '../universal/middleware/validation';
import { generateApolloClient } from '../universal/utils/GqlUtil';
import './XDID';
import HydrationActions from '../universal/actions/hydration';
import LocationActions from '../universal/actions/location';
import AppActions from '../universal/actions/app';
import GoogleActions from '../universal/actions/google';
import {
    EdapEventProvider,
    GtmEventProvider
} from '../universal/utils/EventProviders';
import ExperimentUtil from '../universal/utils/ExperimentUtil';

// Simple polyfill for isNan on ie11
// See https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Number/isNaN
Number.isNaN = Number.isNaN
  || function isNaN(input) {
      // eslint-disable-next-line no-self-compare
      return typeof input === 'number' && input !== input;
  };

const initialState = window.__INITIAL_STATE__;

const composeEnhancers = typeof window === 'object' && window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__
    ? window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__({})
    : compose;

const STORAGE_KEY = 'ulx-storage';
const localStorage = new StorageMiddleware({ storageKey: STORAGE_KEY });

const seedJoinStorage = ({ location = {}, app = {}, google = {} }) => {
    const { address = {} } = location;
    const { lyp = {} } = app;
    const seeded = {
        [LocationActions.SET_ADDRESS_FIELD]: Object.keys(address)
            .filter((field) => address[field])
            .map((field) => LocationActions.setAddressField(field, address[field]))
    };
    if (lyp.bedroomCount != null) {
        seeded[AppActions.SET_BEDROOM_COUNT] = AppActions.setBedroomCount(lyp.bedroomCount);
    }
    if (lyp.bathroomCount != null) {
        seeded[AppActions.SET_BATHROOM_COUNT] = AppActions.setBathroomCount(lyp.bathroomCount);
    }
    if (google.placeId) {
        seeded[GoogleActions.SET_GOOGLE_PLACE_ID] = GoogleActions.setGooglePlaceId(google.placeId);
    }
    try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(seeded));
    } catch (e) {
        // Local storage isn't available
    }
};

const guardedLocalStorageMiddleware = (store) => (next) => (action) => {
    if (!action || typeof action !== 'object') {
        return next(action);
    }
    return localStorage.generateMiddleware()(store)(next)(action);
};

const middleware = [
    thunk,
    routerMiddleware(history),
    ...ValidationMiddleware,
    guardedLocalStorageMiddleware
];

const enhancer = composeEnhancers(applyMiddleware(...middleware));

const store = createStore(
    createClientSideReducer(history),
    initialState,
    enhancer
);

const apolloOptions = {};
if (typeof window === 'object' && window.ha && window.ha.siteName) {
    // Use both site and siteName because different tools use one or the other.
    apolloOptions.queryParams = {
        siteName: window.ha.siteName,
        site: window.ha.siteName
    };
}
const client = generateApolloClient(apolloOptions);

let SwitchedProvider = EdapEventProvider;

const experimentUtil = new ExperimentUtil(initialState.app.experiments);
if (
    initialState.app.features.isConventionalLodgingFlow
  && experimentUtil.isTestVariant('ULX_GTM_INTEGRATION')
) {
    // Naming is confusign, but if the test is on we use the NON test gtm params
    const gtmParams = experimentUtil.isTestVariant('ULX_GTM_TEST')
        ? {
            gtmId: 'GTM-KDZW85F',
            dataLayerName: 'dataLayer'
        }
        : {
            gtmId: 'GTM-KDZW85F',
            dataLayerName: 'dataLayer',
            auth: 'e_HJNo9_An9dVZms9y_tRA',
            preview: 'env-588'
        };

    TagManager.initialize(gtmParams);
    TagManager.dataLayer(gtmParams);
    // initGtmOptimize();
    SwitchedProvider = GtmEventProvider;
}

if (!initialState.app.features.useEdap) {
    if (typeof window === 'object') {
    // Ideally we would conditionally load edap, but in orderr
    // to do that we would have to refactor this file, the webpack
    // code, and the edapUtils code. This bypasses all of that.
    // We can come back and refactor later if we wish to.
        window.edap.trigger = () => {}; // eat any triggered events.
    }
}

loadableReady(() => {
    ReactDOM.hydrate(
        <ApolloProvider client={client}>
            <Provider store={store}>
                <SwitchedProvider>
                    <App />
                </SwitchedProvider>
            </Provider>
        </ApolloProvider>,
        document.getElementById('root')
    );
});

// /join is a clean entry point — clear stored session state so localStorage
// replay doesn't overwrite server-resolved data (address, beds/baths, etc.).
if (/\/join\/?$/.test(window.location.pathname)) {
    window.localStorage.removeItem(STORAGE_KEY);
    seedJoinStorage(initialState);
}

localStorage.replayActions(store);
// Let the app know we are done hydrating.
store.dispatch(HydrationActions.setHydrationComplete());
